import { useParams, Link } from 'react-router-dom';
import SEOHelmet from '@/components/base/SEOHelmet';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
          <i className="ri-folder-open-line text-2xl"></i>
        </div>
        <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">分类未找到</h1>
        <p className="text-foreground-600 mb-6">请检查链接是否正确，或返回首页浏览内容</p>
        <Link
          to="/"
          className="inline-flex px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm transition-colors whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEOHelmet
        title={category.name}
        description={category.description}
        keywords={`91视频下载,${category.name},网络安全,使用指南`}
        canonicalUrl={`https://91-safety-guide.readdy.ai/category/${category.slug}`}
      />

      {/* Category Header */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
              <i className={`${category.icon} text-2xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">
                {category.name}
              </h1>
              <p className="text-foreground-600">{category.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category List + Sidebar */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Article List */}
          <div className="flex-1">
            {categoryArticles.length > 0 ? (
              <div className="space-y-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group block p-5 rounded-lg bg-background-50 border border-background-200/70 transition-all duration-300"
                  >
                    <div className="flex gap-5">
                      <div className="hidden sm:block w-44 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                          title={article.title}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-500">{article.readTime}</span>
                        </div>
                        <h2 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                          <span className="text-xs text-foreground-400">·</span>
                          <span className="text-xs text-foreground-500">{article.author}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">
                  全部分类
                </h3>
                <ul className="space-y-1">
                  {categories.map((cat) => (
                    <li key={cat.id}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                          cat.slug === slug
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-foreground-700 hover:bg-background-50'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        <span>{cat.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-5 rounded-lg bg-accent-50 border border-accent-200/60">
                <h3 className="font-heading font-semibold text-accent-800 mb-3 text-sm">
                  安全提示
                </h3>
                <p className="text-xs text-accent-700 leading-relaxed">
                  访问任何涉及下载的页面时，请始终核实来源的可靠性。不确定时，不要点击、不要下载、不要输入个人信息。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}